import { useMemo, useState, useEffect, useRef } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { readCollection } from "../features/journey/journeyPresentation";
import {
  readLookahead,
  lookaheadCountLabel,
  lookaheadItemsView,
} from "../features/lookahead/lookaheadPresentation";
import { CountBadge } from "../features/journey/ProjectListStates";
import { classifyWorkflowError } from "../lib/workflowErrors";
import {
  REFINE_CONSEQUENCE,
  decideAfterRefineRefusal,
} from "../features/authority/authorityPresentation";
import { AuthorityRefusal } from "../features/authority/AuthorityRefusal";
import {
  afterRefineProposal,
  readRefineItemProposal,
} from "../features/scopeAi/refineProposalPresentation";
import { RefineProposalReview } from "../features/scopeAi/RefineProposalReview";
import { AuthorityAnnouncer } from "../features/authority/AuthorityAnnouncer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";
import NodeTreeView from "../components/NodeTreeView";
import {
  ArrowLeft,
  Plus,
  FolderTree,
  Calendar,
  Package,
  Users,
  Trash2,
  Edit,
  UserCircle,
  Star,
  Mail,
  Phone,
  Building2,
  Sparkles,
  MessageSquare,
  Clock,
  History,
  FileText,
  X,
  RefreshCw,
  AlertCircle,
  ChevronDown,
  ChevronRight,
  ChevronsUpDown,
  Network,
  Radar,
} from "lucide-react";
import { useProjectContacts, useContacts } from "../hooks/useContacts";
import { Badge } from "../components/ui/badge";
import ScopeIntakeWizard from "../features/scope-intake/ScopeIntakeWizard";
import scopeAIService from "../services/scopeAIService";
import ControlPlaneTab from "../features/project-control/ControlPlaneTab";
import RequirementsTab from "../features/requirements/RequirementsTab";
import ReadinessTab from "../features/readiness/ReadinessTab";
import { useAuth } from "../App";
import BringYourProjectDialog from "../features/import/BringYourProjectDialog";
import BetaInsightsPanel from "../features/telemetry/BetaInsightsPanel";
import {
  canEditSchedule,
  canEditProcurement,
  canViewCommercialInternals,
  isSubcontractor,
} from "../lib/roles";
import { Gauge, ClipboardCheck, PlayCircle, Upload, Activity as ActivityIcon } from "lucide-react";

/**
 * The four figures the lookahead rollup carries. Held in one place so a field
 * the backend stops sending shows as NOT ESTABLISHED in every consumer at
 * once, rather than quietly disappearing from one of them.
 */
const LOOKAHEAD_FIGURES = [
  { field: "ready", label: "Ready", colour: "text-emerald-400" },
  { field: "blocked", label: "Blocked", colour: "text-red-400" },
  { field: "in_progress", label: "In Progress", colour: "text-primary" },
  { field: "undated", label: "Undated", colour: "text-amber-400" },
];

const ProjectDetailPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [project, setProject] = useState(null);
  const [nodes, setNodes] = useState([]);
  const [activities, setActivities] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  // Held beside the arrays, not instead of them: every existing consumer keeps
  // working on a plain array, while the COUNT and the empty state ask the read
  // whether the answer is known.
  const [activitiesRead, setActivitiesRead] = useState(null);
  const [auditRead, setAuditRead] = useState(null);
  const [lookahead, setLookahead] = useState(null);
  /**
   * `lookahead` stays raw because ReadinessTab still consumes it directly.
   * The READ is derived beside it, so the figures and the tab count can ask
   * whether anything was actually established. null means still loading, and
   * a read that is still loading establishes nothing — which is correct, and
   * is why the badge shows (—) rather than (0) before the answer arrives.
   */
  const lookaheadRead = useMemo(
    () =>
      lookahead === null
        ? null
        : readLookahead(lookahead, { requestFailed: Boolean(lookahead.failed) }),
    [lookahead],
  );
  const lookaheadItems = lookaheadRead?.state === "READ" ? lookaheadRead.items : [];
  const lookaheadItemsState = lookaheadItemsView(lookaheadRead);
  const [loading, setLoading] = useState(true);
  /**
   * The open tab lives in the URL so other surfaces can deep-link to the
   * facet they mean. Build My Project sends a PM to "what can start" and to
   * the requirements register for a specific package; without this they
   * landed on Overview and had to hunt for the tab the link had named.
   */
  const [tabParams, setTabParams] = useSearchParams();
  /**
   * A tab is only real if this user can see it. `beta-insights` is
   * internal-only, so a shared link naming it would otherwise put an external
   * provider — or anyone the tab is hidden from — on a project page with a
   * selected tab that renders nothing at all. An unreachable tab falls back
   * to Overview rather than to an empty body.
   */
  const requestedTab = tabParams.get("tab") || "overview";
  const activeTab =
    requestedTab === "beta-insights" && !canViewCommercialInternals(user)
      ? "overview"
      : requestedTab;
  const setActiveTab = (next) => {
    const params = new URLSearchParams(tabParams);
    params.set("tab", next);
    setTabParams(params, { replace: true });
  };
  
  // Contacts
  const { contacts: projectContacts, primaryContact, addContact, removeContact, setPrimary, refresh: refreshContacts } = useProjectContacts(projectId);
  const { contacts: allContacts, contactTypes } = useContacts();
  const [isAddContactOpen, setIsAddContactOpen] = useState(false);
  const [selectedContactId, setSelectedContactId] = useState("");
  const [contactRole, setContactRole] = useState("");
  
  // AI Plan Generator
  const [isScopeWizardOpen, setIsScopeWizardOpen] = useState(false);
  
  // Plan Feedback
  const [planFeedback, setPlanFeedback] = useState([]);
  const [feedbackPopoverOpen, setFeedbackPopoverOpen] = useState(null);
  const [newFeedback, setNewFeedback] = useState({ type: "general", text: "" });
  
  // Collapsed phases - tracks which phase IDs are collapsed (default all expanded)
  const [collapsedPhases, setCollapsedPhases] = useState({});
  
  const togglePhaseCollapse = (phaseId) => {
    setCollapsedPhases(prev => ({
      ...prev,
      [phaseId]: !prev[phaseId]
    }));
  };
  
  const collapseAll = () => {
    const phases = activities.filter(a => a.activity_type === 'phase');
    const allCollapsed = {};
    phases.forEach(p => { allCollapsed[p.id] = true; });
    setCollapsedPhases(allCollapsed);
  };
  
  const expandAll = () => {
    setCollapsedPhases({});
  };
  
  // Helper functions
  const getStatusColor = (status) => {
    const colors = {
      proposed: "bg-blue-500/20 text-blue-400 border-blue-500/30",
      active: "bg-green-500/20 text-green-400 border-green-500/30",
      on_hold: "bg-amber-500/20 text-amber-400 border-amber-500/30",
      completed: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
      cancelled: "bg-red-500/20 text-red-400 border-red-500/30",
    };
    return colors[status] || "bg-muted text-muted-foreground";
  };
  
  // Dialogs
  const [isNodeDialogOpen, setIsNodeDialogOpen] = useState(false);
  const [isActivityDialogOpen, setIsActivityDialogOpen] = useState(false);
  const [isEditProjectOpen, setIsEditProjectOpen] = useState(false);
  const [isImportOpen, setIsImportOpen] = useState(false);
  /**
   * Bumped when an import writes. Tabs that keep their own list — the
   * requirements register above all — re-read on the change, because an
   * import that lands invisibly is indistinguishable from one that failed.
   */
  const [importedAt, setImportedAt] = useState(0);
  
  // Form states
  const [nodeForm, setNodeForm] = useState({
    name: "",
    node_type: "phase",
    parent_node_id: "",
  });
  const [activityForm, setActivityForm] = useState({
    name: "",
    activity_type: "construction",
    duration_hours_planned: 8,
    project_node_id: "",
  });
  const [projectForm, setProjectForm] = useState({});

  /*
    The guard reads the CURRENT project from a ref rather than from the closure
    variable, because the closure captures the id as it was when the request
    started — which is exactly the stale value the race is about.
  */
  const projectIdRef = useRef(projectId);
  projectIdRef.current = projectId;

  useEffect(() => {
    fetchProjectData();
  }, [projectId]);

  const fetchProjectData = async () => {
    /**
     * THE PROJECT-SWITCH INVARIANT — defence in depth, not a fixed bug.
     *
     * BE PRECISE ABOUT WHAT THIS DOES AND DOES NOT PROVE. This function
     * performs six sequential awaits from an effect keyed on `projectId` with
     * no cleanup, so a project switch mid-flight WOULD let the slower, older
     * response call setProject last and paint Project A's name, budget and
     * work breakdown under Project B's URL.
     *
     * That switch is currently UNREACHABLE. Every in-app navigation to
     * /projects/:id originates on a different route — the dashboard, the
     * project list, approvals, opportunities, the bid workspace — and this
     * page links only to its own sub-routes. So React unmounts between
     * projects and no instance ever receives two projects' responses.
     *
     * The guard is therefore not proven in a browser and is not claimed to be:
     * the rule is pinned in features/journey/journeyPresentation.test.js. It
     * is here because the page has no cancellation of any kind, so the day a
     * project-to-project link is added the bleed is immediate and silent.
     *
     * `askedAbout` is the project this call is about. Every write below is
     * dropped unless that is still the project on screen. It is an ownership
     * test, not a timestamp: two reads for the SAME project may resolve in any
     * order without harm, and a stale read for a DIFFERENT one never wins.
     */
    const askedAbout = projectId;
    const stillOnScreen = () => projectIdRef.current === askedAbout;

    try {
      // Fetch project data - nodes endpoint may not exist yet
      const projectRes = await axios.get(`${API}/projects/${projectId}`);
      if (!stillOnScreen()) return;
      setProject(projectRes.data);
      setProjectForm(projectRes.data);
      
      // Try to fetch nodes (may not exist)
      try {
        const nodesRes = await axios.get(`${API}/projects/${projectId}/nodes`);
        if (!stillOnScreen()) return;
        setNodes(nodesRes.data || []);
      } catch {
        if (!stillOnScreen()) return;
        setNodes([]);
      }
      
      /*
        Fetch activities.

        A failed read is recorded AS a failed read. It used to set [], and the
        tab then printed "Work Breakdown Structure (0)" — this project has no
        work breakdown — for a request that never answered.
      */
      try {
        const activitiesRes = await axios.get(`${API}/projects/${projectId}/activities`);
        if (!stillOnScreen()) return;
        const read = readCollection(activitiesRes.data);
        setActivitiesRead(read);
        setActivities(read.state === "READ" ? read.items : []);
      } catch {
        if (!stillOnScreen()) return;
        setActivitiesRead(readCollection(null, { requestFailed: true }));
        setActivities([]);
      }
      
      // Fetch plan feedback
      try {
        const feedbackRes = await scopeAIService.getFeedback(projectId);
        if (!stillOnScreen()) return;
        setPlanFeedback(feedbackRes.feedback || []);
      } catch {
        if (!stillOnScreen()) return;
        setPlanFeedback([]);
      }

      // Fetch lookahead schedule. A failure is recorded as a failure — null
      // means "still loading" to the tabs that consume this, so a swallowed
      // error must not masquerade as that.
      try {
        const lookaheadRes = await axios.get(`${API}/projects/${projectId}/lookahead?days=14`);
        if (!stillOnScreen()) return;
        setLookahead(lookaheadRes.data);
      } catch {
        if (!stillOnScreen()) return;
        setLookahead({ failed: true, rollup: null, items: [] });
      }

      // Fetch project activity/audit feed. Same rule as activities: a failed
      // read must not print "Activity Feed (0)".
      try {
        const auditRes = await axios.get(`${API}/audit/projects/${projectId}`);
        if (!stillOnScreen()) return;
        const read = readCollection(auditRes.data);
        setAuditRead(read);
        setAuditLog(read.state === "READ" ? read.items : []);
      } catch {
        if (!stillOnScreen()) return;
        setAuditRead(readCollection(null, { requestFailed: true }));
        setAuditLog([]);
      }
    } catch (error) {
      if (!stillOnScreen()) return;
      toast.error("Failed to load project data");
      navigate("/projects");
    } finally {
      if (stillOnScreen()) setLoading(false);
    }
  };

  // Feedback handlers
  const handleAddFeedback = async (itemType, itemName) => {
    if (!newFeedback.text.trim()) {
      toast.error("Please enter feedback");
      return;
    }
    
    try {
      await scopeAIService.addFeedback(projectId, {
        item_type: itemType,
        item_name: itemName,
        feedback_type: newFeedback.type,
        feedback_text: newFeedback.text,
        suggested_value: newFeedback.suggestedValue || null,
      });
      
      // Refresh feedback
      const feedbackRes = await scopeAIService.getFeedback(projectId);
      setPlanFeedback(feedbackRes.feedback || []);
      
      setFeedbackPopoverOpen(null);
      setNewFeedback({ type: "general", text: "" });
      toast.success("Feedback added");
    } catch (error) {
      toast.error("Failed to add feedback");
    }
  };
  
  const handleRemoveFeedback = async (feedbackId) => {
    try {
      await scopeAIService.removeFeedback(projectId, feedbackId);
      setPlanFeedback(prev => prev.filter(f => f.id !== feedbackId));
      toast.success("Feedback removed");
    } catch (error) {
      toast.error("Failed to remove feedback");
    }
  };
  
  const [refining, setRefining] = useState(false);
  /**
   * THE DELIBERATE CONFIRMATION, ON THE SURFACE THAT ACTUALLY REFINES.
   *
   * AUTHORITY-CONTAINMENT-0 made an unconfirmed refine return 428
   * `confirmation_required` BEFORE the model call. This button was never
   * updated, so since that merged it has sent no confirm, received the 428,
   * and passed the OBJECT detail straight to toast.error -- rendering
   * "[object Object]" and never refining anything. The gate existed on the
   * server and was unreachable in the product.
   *
   * `null` = no pending gate. An object = the server is holding, and a second
   * explicit human act is required. Never set from a response automatically.
   */
  const [refineHold, setRefineHold] = useState(null);
  const [refineProposal, setRefineProposal] = useState(null);
  const [refineRefusal, setRefineRefusal] = useState(null);
  const refineInFlight = useRef(false);
  /**
   * The gate replaces the button the user just pressed with a NEW decision,
   * several lines further down a long page. A sighted user sees it appear; a
   * keyboard user is left on a button whose meaning has changed, and a screen
   * reader is told nothing at all. So the gate is announced through a
   * persistent live region, and focus is moved into it when it opens.
   */
  const refineGateRef = useRef(null);
  const refineGateOpen = Boolean(refineHold);
  useEffect(() => {
    if (refineGateOpen && refineGateRef.current) refineGateRef.current.focus();
  }, [refineGateOpen]);

  // CALL THIS WITH AN EXPLICIT ARGUMENT. `onClick={handleRefinePlan}` passes
  // the CLICK EVENT as the first argument, so `confirm` becomes a truthy
  // object and the FIRST click sends confirm=true -- which makes the 428 gate
  // unreachable while looking perfectly wired. A browser test caught exactly
  // that; keep the call sites as `() => handleRefinePlan(false|true)`.
  const handleRefinePlan = async (confirm = false) => {
    if (planFeedback.length === 0) {
      toast.error("No feedback to apply");
      return;
    }
    // Synchronous: React state is async, so `disabled={refining}` alone lets
    // two clicks in one tick both through, and a confirmed destructive call
    // must be sent exactly once.
    if (refineInFlight.current) return;
    refineInFlight.current = true;

    setRefining(true);
    setRefineRefusal(null);
    try {
      // Targeted refinement -- only items carrying feedback. `confirm` is
      // false on the first call ON PURPOSE, so the 428 is reachable.
      const result = await scopeAIService.refineItems(projectId, { confirm });
      setRefineHold(null);
      
      // PROPOSE-ONLY: the backend returns an UNAPPLIED proposal. The reader
      // fails closed — a payload that cannot prove it is an unapplied
      // proposal is refused, including one that claims it WAS applied.
      const reading = readRefineItemProposal(result);
      if (reading.readable) {
        const next = afterRefineProposal();
        setRefineProposal(reading.proposal);
        toast.success(next.toast);
        // DECIDED in the presenter, OBEYED here: a proposal changed nothing,
        // so activities are not refetched as though they had, and feedback
        // is not cleared as though someone acted on it.
        if (next.refreshActivities) {
          const activitiesRes = await axios.get(`${API}/projects/${projectId}/activities`);
          setActivities(activitiesRes.data || []);
        }
        if (next.clearFeedback) {
          setPlanFeedback([]);
        }
      } else {
        toast.error(reading.reason || "The refinement response could not be verified.");
      }
    } catch (error) {
      // The decision is made by the same pure function the authority module
      // uses, so "428 never auto-confirms" and "409 never auto-retries" hold
      // here identically. Nothing below resends.
      const next = decideAfterRefineRefusal(classifyWorkflowError(error));
      if (next.needsHuman) {
        setRefineHold({ heading: REFINE_CONSEQUENCE.heading });
      } else {
        setRefineHold(null);
        setRefineRefusal(next.refusal);
      }
    } finally {
      refineInFlight.current = false;
      setRefining(false);
    }
  };
  
  const getFeedbackForItem = (itemName) => {
    return planFeedback.filter(f => f.item_name === itemName);
  };

  const handleCreateNode = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(
        `${API}/projects/${projectId}/nodes`,
        nodeForm
      );
      setNodes([...nodes, response.data]);
      setIsNodeDialogOpen(false);
      setNodeForm({ name: "", node_type: "phase", parent_node_id: "" });
      toast.success("Node created successfully");
    } catch (error) {
      toast.error("Failed to create node");
    }
  };

  const handleCreateActivity = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(
        `${API}/projects/${projectId}/activities`,
        activityForm
      );
      setActivities([...activities, response.data]);
      setIsActivityDialogOpen(false);
      setActivityForm({
        name: "",
        activity_type: "construction",
        duration_hours_planned: 8,
        project_node_id: "",
      });
      toast.success("Activity created successfully");
    } catch (error) {
      toast.error("Failed to create activity");
    }
  };

  const handleUpdateProject = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.put(`${API}/projects/${projectId}`, projectForm);
      setProject(response.data);
      setIsEditProjectOpen(false);
      toast.success("Project updated successfully");
    } catch (error) {
      toast.error("Failed to update project");
    }
  };

  const handleDeleteNode = async (nodeId) => {
    if (!window.confirm("Delete this node?")) return;
    try {
      await axios.delete(`${API}/projects/${projectId}/nodes/${nodeId}`);
      setNodes(nodes.filter((n) => n.id !== nodeId));
      toast.success("Node deleted");
    } catch (error) {
      toast.error("Failed to delete node");
    }
  };

  const handleDeleteActivity = async (activityId) => {
    if (!window.confirm("Delete this activity?")) return;
    try {
      await axios.delete(`${API}/projects/${projectId}/activities/${activityId}`);
      setActivities(activities.filter((a) => a.id !== activityId));
      toast.success("Activity deleted");
    } catch (error) {
      toast.error("Failed to delete activity");
    }
  };

  const handleActivityStatusChange = async (activityId, newStatus) => {
    try {
      const response = await axios.put(
        `${API}/projects/${projectId}/activities/${activityId}`,
        { status: newStatus }
      );
      setActivities(
        activities.map((a) => (a.id === activityId ? response.data : a))
      );
      toast.success("Activity status updated");
    } catch (error) {
      toast.error("Failed to update activity");
    }
  };

  // Build tree structure from flat nodes
  const buildNodeTree = (nodes, parentId = null) => {
    return nodes
      .filter((n) => n.parent_node_id === parentId)
      .map((node) => ({
        ...node,
        children: buildNodeTree(nodes, node.id),
      }));
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      not_started: "bg-muted text-muted-foreground",
      ready: "bg-primary/20 text-primary",
      in_progress: "bg-amber-500/20 text-amber-400",
      blocked: "bg-destructive/20 text-destructive",
      complete: "bg-emerald-500/20 text-emerald-400",
    };
    return (
      <span
        className={`px-2 py-1 rounded text-xs font-medium ${
          statusClasses[status] || statusClasses.not_started
        }`}
      >
        {status?.replace("_", " ")}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-pulse text-primary text-xl">Loading project...</div>
      </div>
    );
  }

  if (!project) {
    return null;
  }

  const nodeTree = buildNodeTree(nodes);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="project-detail-page">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-4 min-w-0">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/projects")}
            className="h-10 w-10"
          >
            <ArrowLeft size={20} />
          </Button>
          <div className="min-w-0">
            {/* The title, its code and its two state badges wrap rather than
                riding off the side of a phone. */}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 min-w-0">
              <h1 className="text-2xl font-bold font-heading break-words">{project.name}</h1>
              {project.project_code && (
                <span className="text-sm font-mono text-muted-foreground px-2 py-1 bg-muted rounded">
                  {project.project_code}
                </span>
              )}
              <Badge variant="outline" className={`capitalize ${getStatusColor(project.status)}`}>
                {project.status?.replace(/_/g, ' ')}
              </Badge>
              <Badge variant="outline" className="bg-primary/10 text-primary border-primary/30 capitalize">
                {project.stage?.replace(/_/g, ' ')}
              </Badge>
            </div>
            <p className="text-muted-foreground mt-1">
              {project.client_name && `${project.client_name} • `}
              {project.formatted_address || `${project.city}, ${project.state}`}
            </p>
          </div>
        </div>
        {/* Wraps: on a phone this row of actions is 500px wide and would push
            the page sideways, which is the one thing a field user cannot
            recover from. */}
        <div className="flex flex-wrap gap-2 justify-end">
          <Button 
            variant="outline" 
            className="border-primary/50 text-primary hover:bg-primary/10"
            onClick={() => setIsScopeWizardOpen(true)}
          >
            <Sparkles className="mr-2 h-4 w-4" />
            Generate AI Plan
          </Button>
          {/* Bring your project — the door for a GC who already has the data
              somewhere else. Offered when the role can import ANY of the three
              kinds; the dialog explains a refusal per kind, and the backend
              stays authoritative either way. */}
          {(canEditSchedule(user) || canEditProcurement(user)) && (
            <Button
              variant="outline"
              onClick={() => setIsImportOpen(true)}
              data-testid="bring-your-project-btn"
            >
              <Upload className="mr-2 h-4 w-4" />
              Bring your project
            </Button>
          )}
          <Dialog open={isEditProjectOpen} onOpenChange={setIsEditProjectOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="edit-project-btn">
                <Edit className="mr-2 h-4 w-4" />
                Edit Project
              </Button>
            </DialogTrigger>
          <DialogContent className="bg-card">
            <DialogHeader>
              <DialogTitle>Edit Project</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpdateProject} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Project Name</Label>
                <Input
                  value={projectForm.name || ""}
                  onChange={(e) =>
                    setProjectForm({ ...projectForm, name: e.target.value })
                  }
                  className="bg-muted/50"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select
                    value={projectForm.status || "proposed"}
                    onValueChange={(value) =>
                      setProjectForm({ ...projectForm, status: value })
                    }
                  >
                    <SelectTrigger className="bg-muted/50">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="proposed">Proposed</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="on_hold">On Hold</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Stage</Label>
                  <Select
                    value={projectForm.stage || "estimating"}
                    onValueChange={(value) =>
                      setProjectForm({ ...projectForm, stage: value })
                    }
                  >
                    <SelectTrigger className="bg-muted/50">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="estimating">Estimating</SelectItem>
                      <SelectItem value="design">Design</SelectItem>
                      <SelectItem value="permitting">Permitting</SelectItem>
                      <SelectItem value="pre_construction">Preconstruction</SelectItem>
                      <SelectItem value="construction">Construction</SelectItem>
                      <SelectItem value="punch_list">Punch List</SelectItem>
                      <SelectItem value="closeout">Closeout</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Progress (%)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={projectForm.percent_complete || 0}
                  onChange={(e) =>
                    setProjectForm({
                      ...projectForm,
                      percent_complete: parseFloat(e.target.value) || 0,
                    })
                  }
                  className="bg-muted/50"
                />
              </div>
              <div className="flex justify-end gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsEditProjectOpen(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" className="bg-primary">
                  Save Changes
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Progress Overview */}
      <Card className="stat-card">
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div>
              <p className="text-sm text-muted-foreground">Status</p>
              <p className="text-lg font-semibold capitalize mt-1">
                {project.status?.replace("_", " ")}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Stage</p>
              <p className="text-lg font-semibold capitalize mt-1">{project.stage}</p>
            </div>
            <div className="md:col-span-2">
              <p className="text-sm text-muted-foreground mb-2">Overall Progress</p>
              <div className="flex items-center gap-4">
                <Progress value={project.percent_complete || 0} className="flex-1 h-3" />
                <span className="text-lg font-bold font-mono">
                  {project.percent_complete?.toFixed(0) || 0}%
                </span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Command Room Quick Links */}
      <div
        className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4"
        aria-label="Project workspace links"
        data-testid="project-workspace-links"
      >
        <Link to={`/projects/${projectId}/gantt`} className="rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
          <Card className="stat-card h-full cursor-pointer hover:border-primary/50 transition-colors">
            <CardContent className="p-4 flex items-center gap-3">
              <Calendar className="h-8 w-8 text-primary" />
              <div>
                <p className="font-semibold">Schedule / Gantt</p>
                <p className="text-xs text-muted-foreground">Timeline, dependencies, exports</p>
              </div>
            </CardContent>
          </Card>
        </Link>
        <Link to={`/projects/${projectId}/documents`} className="rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
          <Card className="stat-card h-full cursor-pointer hover:border-primary/50 transition-colors">
            <CardContent className="p-4 flex items-center gap-3">
              <FileText className="h-8 w-8 text-primary" />
              <div>
                <p className="font-semibold">Documents</p>
                <p className="text-xs text-muted-foreground">Files, review status, versions</p>
              </div>
            </CardContent>
          </Card>
        </Link>
        {/* InternalOnly uses this same subcontractor classification. These
            links are presentation, never authority: the destination routes
            and backend remain the enforcement boundaries. */}
        {!isSubcontractor(user) && (
          <>
            <Link to={`/exchange?project_id=${projectId}`} className="rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
              <Card className="stat-card h-full cursor-pointer hover:border-primary/50 transition-colors">
                <CardContent className="p-4 flex items-center gap-3">
                  <Network className="h-8 w-8 text-primary" />
                  <div>
                    <p className="font-semibold">Coverage &amp; Sourcing</p>
                    <p className="text-xs text-muted-foreground">Trade coverage, packages, provider options</p>
                  </div>
                </CardContent>
              </Card>
            </Link>
            <Link to={`/procurement?project_id=${projectId}`} className="rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
              <Card className="stat-card h-full cursor-pointer hover:border-primary/50 transition-colors">
                <CardContent className="p-4 flex items-center gap-3">
                  <Package className="h-8 w-8 text-primary" />
                  <div>
                    <p className="font-semibold">Bids / Procurement</p>
                    <p className="text-xs text-muted-foreground">Vendors, bids, procurement items</p>
                  </div>
                </CardContent>
              </Card>
            </Link>
            <Link to={`/control-tower?project_id=${projectId}`} className="rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
              <Card className="stat-card h-full cursor-pointer hover:border-primary/50 transition-colors">
                <CardContent className="p-4 flex items-center gap-3">
                  <Radar className="h-8 w-8 text-primary" />
                  <div>
                    <p className="font-semibold">Control Tower</p>
                    <p className="text-xs text-muted-foreground">Readiness, constraints, execution signals</p>
                  </div>
                </CardContent>
              </Card>
            </Link>
          </>
        )}
        <Link to="/workforce" className="rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
          <Card className="stat-card h-full cursor-pointer hover:border-primary/50 transition-colors">
            <CardContent className="p-4 flex items-center gap-3">
              <Users className="h-8 w-8 text-primary" />
              <div>
                <p className="font-semibold">Team / Workforce</p>
                <p className="text-xs text-muted-foreground">Contractors, crews, bookings</p>
              </div>
            </CardContent>
          </Card>
        </Link>
      </div>

      {/*
        Bring your project. An import writes into the same register that
        decides whether work can start, so on success the page re-reads rather
        than leaving a stale picture beside new records.
      */}
      <BringYourProjectDialog
        open={isImportOpen}
        onOpenChange={setIsImportOpen}
        projectId={projectId}
        onImported={(kind) => {
          fetchProjectData();
          setImportedAt((n) => n + 1);
          // Land the user where the new records actually are.
          if (kind === "schedule") setActiveTab("activities");
          if (kind === "requirements") setActiveTab("requirements");
        }}
      />

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        {/*
          Eight tabs in one inline row measure 1157px — nearly three phone
          screens — and an unwrapped TabsList takes the whole page with it.
          Wrapping keeps every tab reachable at any width; `h-auto` lets the
          rows stack instead of being clipped to a single 36px strip.
        */}
        <TabsList className="bg-muted/50 flex-wrap h-auto">
          <TabsTrigger value="overview" className="data-[state=active]:bg-primary/20">
            <Gauge className="mr-2 h-4 w-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="readiness" className="data-[state=active]:bg-primary/20">
            <PlayCircle className="mr-2 h-4 w-4" />
            What Can Start?
          </TabsTrigger>
          <TabsTrigger value="requirements" className="data-[state=active]:bg-primary/20">
            <ClipboardCheck className="mr-2 h-4 w-4" />
            Requirements
          </TabsTrigger>
          <TabsTrigger value="activities" className="data-[state=active]:bg-primary/20">
            <FolderTree className="mr-2 h-4 w-4" />
            Work Breakdown Structure <CountBadge read={activitiesRead} testId="wbs-count" />
          </TabsTrigger>
          <TabsTrigger value="lookahead" className="data-[state=active]:bg-primary/20">
            <Clock className="mr-2 h-4 w-4" />
            Lookahead <CountBadge count={lookaheadCountLabel(lookaheadRead)} testId="lookahead-count" />
          </TabsTrigger>
          <TabsTrigger value="contacts" className="data-[state=active]:bg-primary/20">
            <UserCircle className="mr-2 h-4 w-4" />
            Contacts
          </TabsTrigger>
          <TabsTrigger value="activity-feed" className="data-[state=active]:bg-primary/20">
            <History className="mr-2 h-4 w-4" />
            Activity Feed <CountBadge read={auditRead} testId="audit-count" />
          </TabsTrigger>
          {/* Beta insights carries commercial funnel counts, and the endpoint
              has NO role gate of its own — it uses bare get_current_user — so
              this is the only thing keeping it off an external provider's
              screen. */}
          {canViewCommercialInternals(user) && (
            <TabsTrigger value="beta-insights" className="data-[state=active]:bg-primary/20">
              <ActivityIcon className="mr-2 h-4 w-4" />
              Beta Insights
            </TabsTrigger>
          )}
        </TabsList>

        {/* Overview — project control plane */}
        <TabsContent value="overview" className="space-y-4">
          <ControlPlaneTab
            projectId={projectId}
            activities={activities}
            lookahead={lookahead}
            lookaheadRead={lookaheadRead}
            onNavigateTab={setActiveTab}
          />
        </TabsContent>

        {/* Pilot telemetry — internal product diagnostics, not customer
            Commercial IA. Its future home is Platform Control. */}
        {canViewCommercialInternals(user) && (
          <TabsContent value="beta-insights" className="space-y-4">
            <BetaInsightsPanel projectId={projectId} />
          </TabsContent>
        )}

        {/* What can start? — execution readiness */}
        <TabsContent value="readiness" className="space-y-4">
          <ReadinessTab projectId={projectId} user={user} lookahead={lookahead} />
        </TabsContent>

        {/* Requirements register */}
        <TabsContent value="requirements" className="space-y-4">
          <RequirementsTab
            projectId={projectId}
            user={user}
            activities={activities}
            reloadToken={importedAt}
          />
        </TabsContent>

        {/* Work Breakdown Structure Tab (formerly Activities) */}
        <TabsContent value="activities" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold font-heading">Work Breakdown Structure</h2>
            <Dialog open={isActivityDialogOpen} onOpenChange={setIsActivityDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  className="bg-primary hover:bg-primary/90"
                  data-testid="add-activity-btn"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Add Task
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-card">
                <DialogHeader>
                  <DialogTitle>Add Task</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateActivity} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label>Activity Name</Label>
                    <Input
                      value={activityForm.name}
                      onChange={(e) =>
                        setActivityForm({ ...activityForm, name: e.target.value })
                      }
                      placeholder="Pour concrete foundation"
                      required
                      className="bg-muted/50"
                      data-testid="activity-name-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Type</Label>
                      <Select
                        value={activityForm.activity_type}
                        onValueChange={(value) =>
                          setActivityForm({ ...activityForm, activity_type: value })
                        }
                      >
                        <SelectTrigger className="bg-muted/50">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="construction">Construction</SelectItem>
                          <SelectItem value="inspection">Inspection</SelectItem>
                          <SelectItem value="delivery_window">Delivery</SelectItem>
                          <SelectItem value="milestone">Milestone</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Duration (hours)</Label>
                      <Input
                        type="number"
                        min="1"
                        value={activityForm.duration_hours_planned}
                        onChange={(e) =>
                          setActivityForm({
                            ...activityForm,
                            duration_hours_planned: parseInt(e.target.value) || 8,
                          })
                        }
                        className="bg-muted/50 font-mono"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>WBS Node (Optional)</Label>
                    <Select
                      value={activityForm.project_node_id || "none"}
                      onValueChange={(value) =>
                        setActivityForm({
                          ...activityForm,
                          project_node_id: value === "none" ? null : value,
                        })
                      }
                    >
                      <SelectTrigger className="bg-muted/50">
                        <SelectValue placeholder="Select WBS node" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No Node</SelectItem>
                        {nodes.map((n) => (
                          <SelectItem key={n.id} value={n.id}>
                            {n.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex justify-end gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setIsActivityDialogOpen(false)}
                    >
                      Cancel
                    </Button>
                    <Button type="submit" className="bg-primary" data-testid="submit-activity-btn">
                      Add Activity
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <Card className="stat-card">
            <CardContent className="p-0">
              {activities.length > 0 ? (
                <div>
                  {/* Feedback Summary Banner */}
                  {planFeedback.length > 0 && (
                    <div className="p-3 bg-amber-500/10 border-b border-amber-500/30 flex items-center justify-between">
                      <div className="flex items-center gap-2 text-amber-400">
                        <AlertCircle className="h-4 w-4" />
                        <span className="text-sm font-medium">{planFeedback.length} feedback item(s) pending</span>
                      </div>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="border-amber-500/50 text-amber-400 hover:bg-amber-500/20"
                        onClick={() => handleRefinePlan(false)}
                        aria-disabled={refining ? "true" : "false"}
                        aria-busy={refining ? "true" : "false"}
                      >
                        {refining ? (
                          <>
                            <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                            Refining Plan...
                          </>
                        ) : (
                          <>
                            <RefreshCw className="h-3 w-3 mr-1" />
                            Regenerate with Feedback
                          </>
                        )}
                      </Button>
                    </div>
                  )}

                  {/* An unapplied AI proposal under review. Rendering it is
                      the whole point of propose-only: the user sees what was
                      proposed, sees it is NOT applied, and closes it. */}
                  {refineProposal && (
                    <RefineProposalReview
                      proposal={refineProposal}
                      onDismiss={() => setRefineProposal(null)}
                    />
                  )}

                  {/* Mounted whatever the state, so a change to its text is
                      a change an assistive technology is already observing.
                      Mounting the gate and its announcement together says
                      nothing -- see AuthorityAnnouncer. */}
                  <AuthorityAnnouncer
                    message={
                      refineRefusal
                        ? [refineRefusal.heading, refineRefusal.lead].filter(Boolean).join('. ')
                        : refineHold
                        ? [REFINE_CONSEQUENCE.heading, ...REFINE_CONSEQUENCE.body].join(' ')
                        : ''
                    }
                    urgent={Boolean(refineRefusal)}
                  />

                  {/* The server is holding. A SECOND, separate human act is
                      required -- this is never confirmed automatically. */}
                  {refineHold && (
                    <div
                      ref={refineGateRef}
                      tabIndex={-1}
                      data-testid="refine-consequence"
                      role="group"
                      aria-labelledby="refine-consequence-heading"
                      className="p-3 border-b border-amber-500/30 bg-amber-500/10 space-y-2 focus:outline-none focus:ring-2 focus:ring-amber-400"
                    >
                      <p id="refine-consequence-heading" className="text-sm font-semibold text-amber-300">
                        {REFINE_CONSEQUENCE.heading}
                      </p>
                      {REFINE_CONSEQUENCE.body.map((line) => (
                        <p key={line} className="text-sm text-amber-200">{line}</p>
                      ))}
                      <p className="text-xs text-amber-200/80">{REFINE_CONSEQUENCE.reassurance}</p>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          variant="destructive"
                          data-testid="refine-confirm"
                          className="aria-disabled:opacity-50 aria-disabled:cursor-not-allowed"
                          aria-disabled={refining ? "true" : "false"}
                          aria-busy={refining ? "true" : "false"}
                          onClick={() => handleRefinePlan(true)}
                        >
                          {REFINE_CONSEQUENCE.confirmLabel}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid="refine-cancel"
                          className="aria-disabled:opacity-50 aria-disabled:cursor-not-allowed"
                          aria-disabled={refining ? "true" : "false"}
                          onClick={() => {
                            if (refining) return;
                            setRefineHold(null);
                          }}
                        >
                          {REFINE_CONSEQUENCE.cancelLabel}
                        </Button>
                      </div>
                    </div>
                  )}

                  {refineRefusal && (
                    <div className="p-3 border-b border-slate-500/30">
                      <AuthorityRefusal refusal={refineRefusal} />
                    </div>
                  )}
                  
                  {/* Group activities by type for better display */}
                  {(() => {
                    const phases = activities.filter(a => a.activity_type === 'phase');
                    const tasks = activities.filter(a => a.activity_type !== 'phase');
                    
                    // Group tasks by their parent phase
                    const getTasksForPhase = (phaseId) => {
                      return tasks.filter(t => t.parent_activity_id === phaseId);
                    };
                    
                    // Get orphan tasks (tasks without a parent)
                    const orphanTasks = tasks.filter(t => !t.parent_activity_id);
                    
                    return (
                      <div className="divide-y divide-border">
                        {/* Phases with nested tasks */}
                        {phases.length > 0 && (
                          <div className="p-4 space-y-3">
                            {/* Expand/Collapse All Controls */}
                            <div className="flex justify-end gap-2 mb-2">
                              <Button variant="ghost" size="sm" onClick={expandAll} className="text-xs h-7">
                                <ChevronsUpDown className="h-3 w-3 mr-1" />
                                Expand All
                              </Button>
                              <Button variant="ghost" size="sm" onClick={collapseAll} className="text-xs h-7">
                                <ChevronsUpDown className="h-3 w-3 mr-1" />
                                Collapse All
                              </Button>
                            </div>
                            
                            {phases.map((phase, idx) => {
                              const phaseTasks = getTasksForPhase(phase.id);
                              const itemFeedback = getFeedbackForItem(phase.name);
                              const hasFeedback = itemFeedback.length > 0;
                              const isCollapsed = collapsedPhases[phase.id];
                              
                              return (
                                <div key={phase.id} className={`rounded-lg border ${hasFeedback ? 'border-amber-500/30 bg-amber-500/5' : 'border-border bg-muted/20'}`}>
                                  {/* Phase Header - Clickable to expand/collapse */}
                                  <div 
                                    className="p-3 flex items-center justify-between cursor-pointer hover:bg-muted/30 transition-colors"
                                    onClick={() => phaseTasks.length > 0 && togglePhaseCollapse(phase.id)}
                                  >
                                    <div className="flex items-center gap-3">
                                      {/* Expand/Collapse Icon */}
                                      {phaseTasks.length > 0 ? (
                                        <span className="text-muted-foreground">
                                          {isCollapsed ? (
                                            <ChevronRight className="h-4 w-4" />
                                          ) : (
                                            <ChevronDown className="h-4 w-4" />
                                          )}
                                        </span>
                                      ) : (
                                        <span className="w-4" /> 
                                      )}
                                      <span className="h-8 w-8 rounded-full bg-primary/20 flex items-center justify-center text-primary font-semibold text-sm">
                                        {idx + 1}
                                      </span>
                                      <div>
                                        <div className="flex items-center gap-2">
                                          <p className="font-medium">
                                            {phase.wbs_code && <span className="text-primary font-mono mr-2">{phase.wbs_code}</span>}
                                            {phase.name}
                                          </p>
                                          {phaseTasks.length > 0 && (
                                            <span className="text-xs text-muted-foreground">
                                              ({phaseTasks.length} task{phaseTasks.length !== 1 ? 's' : ''})
                                            </span>
                                          )}
                                        </div>
                                        {phase.description && (
                                          <p className="text-sm text-muted-foreground">{phase.description}</p>
                                        )}
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
                                      <span className="text-sm text-muted-foreground font-mono">
                                        {phase.duration_days || 0} days
                                      </span>
                                      {getStatusBadge(phase.status)}
                                      
                                      {/* Feedback Popover */}
                                      <Popover open={feedbackPopoverOpen === phase.id} onOpenChange={(open) => {
                                        setFeedbackPopoverOpen(open ? phase.id : null);
                                        if (!open) setNewFeedback({ type: "general", text: "" });
                                      }}>
                                        <PopoverTrigger asChild>
                                          <Button variant="ghost" size="icon" className="h-8 w-8 relative">
                                            <MessageSquare className={`h-4 w-4 ${hasFeedback ? 'text-amber-400' : ''}`} />
                                            {hasFeedback && (
                                              <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-amber-500 text-[10px] font-bold flex items-center justify-center text-black">
                                                {itemFeedback.length}
                                              </span>
                                            )}
                                          </Button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-80" align="end">
                                          <div className="space-y-3">
                                            <h4 className="font-medium text-sm">Feedback for {phase.name}</h4>
                                            
                                            {itemFeedback.length > 0 && (
                                              <div className="space-y-2 max-h-32 overflow-y-auto">
                                                {itemFeedback.map(fb => (
                                                  <div key={fb.id} className="text-xs bg-muted/50 p-2 rounded flex justify-between items-start gap-2">
                                                    <div>
                                                      <Badge variant="outline" className="text-[10px] mb-1">{fb.feedback_type}</Badge>
                                                      <p>{fb.feedback_text}</p>
                                                    </div>
                                                    <Button variant="ghost" size="icon" className="h-5 w-5 flex-shrink-0" onClick={() => handleRemoveFeedback(fb.id)}>
                                                      <X className="h-3 w-3" />
                                                    </Button>
                                                  </div>
                                                ))}
                                              </div>
                                            )}
                                            
                                            <div className="flex flex-wrap gap-1">
                                              {["Too Long", "Too Short", "Remove", "Split"].map(quick => (
                                                <Button 
                                                  key={quick} 
                                                  variant="outline" 
                                                  size="sm" 
                                                  className="text-xs h-7"
                                                  onClick={() => setNewFeedback({ type: quick.toLowerCase().replace(' ', '_'), text: quick })}
                                                >
                                                  {quick}
                                                </Button>
                                              ))}
                                            </div>
                                            
                                            <Textarea
                                              placeholder="Add specific feedback..."
                                              value={newFeedback.text}
                                              onChange={(e) => setNewFeedback(prev => ({ ...prev, text: e.target.value }))}
                                              className="min-h-[60px] text-sm"
                                            />
                                            
                                            <Button 
                                              size="sm" 
                                              className="w-full"
                                              onClick={() => handleAddFeedback("phase", phase.name)}
                                            >
                                              Add Feedback
                                            </Button>
                                          </div>
                                        </PopoverContent>
                                      </Popover>
                                    </div>
                                  </div>
                                  
                                  {/* Nested Tasks - Collapsible */}
                                  {phaseTasks.length > 0 && !isCollapsed && (
                                    <div className="border-t border-border/50 bg-background/50">
                                      {phaseTasks.map((task, taskIdx) => (
                                        <div 
                                          key={task.id} 
                                          className={`flex items-center justify-between px-4 py-2 ${taskIdx < phaseTasks.length - 1 ? 'border-b border-border/30' : ''}`}
                                        >
                                          <div className="flex items-center gap-3 pl-8">
                                            <span className="text-muted-foreground text-xs">└</span>
                                            <div>
                                              <p className="text-sm">
                                                {task.wbs_code && <span className="text-primary font-mono mr-2">{task.wbs_code}</span>}
                                                {task.name}
                                              </p>
                                              {task.description && (
                                                <p className="text-xs text-muted-foreground">{task.description}</p>
                                              )}
                                            </div>
                                          </div>
                                          <div className="flex items-center gap-3">
                                            <span className="text-xs text-muted-foreground font-mono">
                                              {task.duration_days || 0}d
                                            </span>
                                            <Select
                                              value={task.status}
                                              onValueChange={(value) => handleActivityStatusChange(task.id, value)}
                                            >
                                              <SelectTrigger className="w-[120px] h-7 text-xs">
                                                <SelectValue />
                                              </SelectTrigger>
                                              <SelectContent>
                                                <SelectItem value="not_started">Not Started</SelectItem>
                                                <SelectItem value="in_progress">In Progress</SelectItem>
                                                <SelectItem value="complete">Complete</SelectItem>
                                              </SelectContent>
                                            </Select>
                                            <Button
                                              variant="ghost"
                                              size="icon"
                                              className="h-7 w-7"
                                              onClick={() => handleDeleteActivity(task.id)}
                                            >
                                              <Trash2 size={12} className="text-destructive" />
                                            </Button>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                        
                        {/* Orphan Tasks (tasks without a parent phase) */}
                        {orphanTasks.length > 0 && (
                          <div className="p-4">
                            <h3 className="text-sm font-semibold text-muted-foreground mb-3 uppercase tracking-wide">
                              Other Tasks ({orphanTasks.length})
                            </h3>
                            <div className="space-y-2">
                              {orphanTasks.map((task) => (
                                <div key={task.id} className="flex items-center justify-between p-2 bg-muted/30 rounded">
                                  <div>
                                    <p className="text-sm font-medium">{task.name}</p>
                                    {task.description && (
                                      <p className="text-xs text-muted-foreground">{task.description}</p>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs text-muted-foreground font-mono">{task.duration_days || 0}d</span>
                                    {getStatusBadge(task.status)}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No activities yet. Use &quot;Generate AI Plan&quot; to create a project plan.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="lookahead" className="space-y-4">
          <Card className="stat-card">
            <CardHeader>
              <CardTitle className="font-heading">14-Day Lookahead</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {LOOKAHEAD_FIGURES.map(({ field, label, colour }) => {
                  const fact = lookaheadRead?.rollup?.[field];
                  const established = Boolean(fact?.established);
                  return (
                    <div key={field} className="rounded-lg bg-muted/30 p-4">
                      <p className="text-sm text-muted-foreground">{label}</p>
                      <p
                        className={`text-2xl font-bold ${established ? colour : "text-muted-foreground"}`}
                        data-testid={`lookahead-${field}`}
                        data-established={established ? "true" : "false"}
                      >
                        {established ? String(fact.value) : "Not established"}
                      </p>
                    </div>
                  );
                })}
              </div>
              {lookaheadItems.length > 0 ? (
                <div className="space-y-3">
                  {lookaheadItems.map((item) => (
                    <div key={item.id} className="rounded-lg bg-muted/30 p-4 flex flex-col lg:flex-row lg:items-center justify-between gap-3">
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="font-medium">{item.name}</p>
                          <Badge variant="outline" className="capitalize">{item.status?.replace(/_/g, " ")}</Badge>
                          <Badge className={item.lookahead_status === "blocked" ? "bg-red-500/20 text-red-400" : item.lookahead_status === "in_progress" ? "bg-primary/20 text-primary" : "bg-emerald-500/20 text-emerald-400"}>{item.lookahead_status}</Badge>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">Start: {item.start_dt_planned ? new Date(item.start_dt_planned).toLocaleDateString() : "No planned date"} • Duration: {item.duration_hours_planned || 0}h</p>
                        {item.blockers?.length > 0 && <p className="text-sm text-red-400 mt-1">Blocked by: {item.blockers.map((b) => b.predecessor_name).join(", ")}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div
                  className="text-center py-12 text-muted-foreground"
                  role="status"
                  aria-live="polite"
                  data-testid="lookahead-items-state"
                  data-state={lookaheadItemsState.kind}
                >
                  <Clock className="h-12 w-12 mx-auto mb-4 opacity-50" aria-hidden="true" />
                  <h3 className="font-medium text-foreground">{lookaheadItemsState.heading}</h3>
                  <p className="text-sm mt-1">{lookaheadItemsState.body}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Contacts Tab */}
        <TabsContent value="contacts" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold font-heading">Project Contacts</h2>
            <Dialog open={isAddContactOpen} onOpenChange={setIsAddContactOpen}>
              <DialogTrigger asChild>
                <Button className="bg-primary hover:bg-primary/90">
                  <Plus className="mr-2 h-4 w-4" />
                  Add Contact
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-card">
                <DialogHeader>
                  <DialogTitle className="font-heading">Add Contact to Project</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div>
                    <Label>Select Contact</Label>
                    <Select value={selectedContactId} onValueChange={setSelectedContactId}>
                      <SelectTrigger className="bg-muted/50 mt-1">
                        <SelectValue placeholder="Choose a contact" />
                      </SelectTrigger>
                      <SelectContent>
                        {allContacts
                          .filter(c => !projectContacts.some(pc => pc.contact_id === c.id))
                          .map((contact) => (
                            <SelectItem key={contact.id} value={contact.id}>
                              {contact.full_name} {contact.company ? `(${contact.company})` : ""}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="contact-role">Role on Project (Optional)</Label>
                    <Input
                      id="contact-role"
                      value={contactRole}
                      onChange={(e) => setContactRole(e.target.value)}
                      placeholder="e.g., Project Owner, Site Manager"
                      className="bg-muted/50 mt-1"
                    />
                  </div>
                  <div className="flex justify-end gap-2 pt-4">
                    <Button variant="outline" onClick={() => setIsAddContactOpen(false)}>
                      Cancel
                    </Button>
                    <Button
                      onClick={async () => {
                        if (!selectedContactId) {
                          toast.error("Please select a contact");
                          return;
                        }
                        await addContact(selectedContactId, contactRole || null, projectContacts.length === 0);
                        setSelectedContactId("");
                        setContactRole("");
                        setIsAddContactOpen(false);
                      }}
                      className="bg-primary hover:bg-primary/90"
                    >
                      Add Contact
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {/* Primary Contact */}
          {primaryContact && (
            <Card className="border-primary/30 bg-primary/5">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-primary/20 flex items-center justify-center">
                      <Star className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-xs text-primary font-medium">Primary Contact</p>
                      <h4 className="font-semibold">{primaryContact.contact.full_name}</h4>
                      {primaryContact.role && (
                        <p className="text-xs text-muted-foreground">{primaryContact.role}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-sm">
                    {primaryContact.contact.email && (
                      <a href={`mailto:${primaryContact.contact.email}`} className="flex items-center gap-1 text-muted-foreground hover:text-primary">
                        <Mail className="h-4 w-4" />
                        {primaryContact.contact.email}
                      </a>
                    )}
                    {primaryContact.contact.phone && (
                      <a href={`tel:${primaryContact.contact.phone}`} className="flex items-center gap-1 text-muted-foreground hover:text-primary">
                        <Phone className="h-4 w-4" />
                        {primaryContact.contact.phone}
                      </a>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Contacts List */}
          <Card className="stat-card">
            <CardContent className="p-0">
              {projectContacts.length > 0 ? (
                <div className="divide-y divide-border">
                  {projectContacts.map((pc) => (
                    <div key={pc.id} className="flex items-center justify-between p-4 hover:bg-muted/30">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center">
                          <UserCircle className="h-5 w-5 text-muted-foreground" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium">{pc.contact.full_name}</h4>
                            {pc.is_primary && (
                              <Badge className="bg-primary/20 text-primary text-xs">Primary</Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            {pc.role && <span>{pc.role}</span>}
                            {pc.role && pc.contact.company && <span>•</span>}
                            {pc.contact.company && (
                              <span className="flex items-center gap-1">
                                <Building2 className="h-3 w-3" />
                                {pc.contact.company}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {pc.contact.email && (
                          <a href={`mailto:${pc.contact.email}`} className="p-2 hover:bg-muted rounded">
                            <Mail className="h-4 w-4 text-muted-foreground" />
                          </a>
                        )}
                        {pc.contact.phone && (
                          <a href={`tel:${pc.contact.phone}`} className="p-2 hover:bg-muted rounded">
                            <Phone className="h-4 w-4 text-muted-foreground" />
                          </a>
                        )}
                        {!pc.is_primary && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setPrimary(pc.contact_id)}
                            title="Set as primary"
                          >
                            <Star className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            if (window.confirm(`Remove ${pc.contact.full_name} from this project?`)) {
                              removeContact(pc.contact_id);
                            }
                          }}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <UserCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No contacts assigned to this project.</p>
                  <p className="text-sm">Add contacts to keep track of stakeholders.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="activity-feed" className="space-y-4">
          <Card className="stat-card">
            <CardHeader>
              <CardTitle className="font-heading">Project Activity Feed</CardTitle>
            </CardHeader>
            <CardContent>
              {auditLog.length > 0 ? (
                <div className="space-y-3">
                  {auditLog.map((entry) => (
                    <div key={entry.id} className="flex items-start gap-3 rounded-lg bg-muted/30 p-4">
                      <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <History className="h-4 w-4 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-medium capitalize">{entry.entity_type?.replace(/_/g, " ")}</span>
                          <Badge variant="outline" className="capitalize">{entry.action?.replace(/_/g, " ")}</Badge>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          {entry.created_at ? new Date(entry.created_at).toLocaleString() : "Recent activity"}
                        </p>
                        {entry.changes && Object.keys(entry.changes).length > 0 && (
                          <p className="text-xs text-muted-foreground mt-2 truncate">
                            {Object.entries(entry.changes).slice(0, 4).map(([key, value]) => `${key}: ${typeof value === "object" ? JSON.stringify(value) : value}`).join(" • ")}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <History className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No tracked activity yet.</p>
                  <p className="text-sm">Project, schedule, bid, and procurement changes will appear here.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* AI Scope Intake Wizard */}
      <ScopeIntakeWizard
        project={project}
        isOpen={isScopeWizardOpen}
        onClose={() => setIsScopeWizardOpen(false)}
        onPlanGenerated={(plan) => {
          fetchProjectData();
          toast.success(`Plan created with ${plan.phases?.length || 0} phases!`);
        }}
      />
    </div>
  );
};

export default ProjectDetailPage;
